import React, { useState, useRef } from 'react';
import {
    Box,
    Typography,
    IconButton,
    useTheme,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';

// Import custom SVG icon
import GalleryIcon from '../../assets/gallery.svg?react';
import MicSvgIcon from '../../assets/Mic.svg?react';
import VideoSvgIcon from '../../assets/Video.svg?react';
import AddIcon from '../../assets/Add.svg?react';
import EarPhoneIcon from '../../assets/earphone.svg?react';
import { AppName, getAppName } from '../../utils/commonMethods';

export type MediaType = 'image' | 'video' | 'audio';

interface MediaItem {
    id: string;
    type: 'capture' | 'upload' | 'placeholder';
    thumbnail?: string;
    name?: string;
    original_file_name?: string;
}

interface MediaUploadSectionProps {
    title: string;
    mediaType: MediaType;
    items?: MediaItem[];
    onCapture?: () => void;
    onUpload?: () => void;
    onRemove?: (id: string) => void;
    onPreview?: (item: MediaItem) => void;
    maxItems?: number;
}

const MediaUploadSection: React.FC<MediaUploadSectionProps> = ({
    title,
    mediaType,
    items = [],
    onUpload,
    onRemove,
    onPreview,
    maxItems = 10,
}) => {
    const theme = useTheme();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);
    const isBio = getAppName() === AppName.BioEcologie;
    const brandMain = isBio ? theme.palette.primary.main : (theme.palette.blueColors?.main ?? theme.palette.primary.main);

    // Get the appropriate icon for the media type
    const getMediaIcon = () => {
        switch (mediaType) {
            case 'image':
                return <GalleryIcon style={{ width: '40px', height: '40px', color: theme.palette.primary.main }} />;
            case 'video':
                return <VideoSvgIcon style={{ width: '40px', height: '40px', color: theme.palette.primary.main }} />;
            case 'audio':
                return <MicSvgIcon style={{ width: '40px', height: '40px', color: theme.palette.primary.main }} />;
            default:
                return <AddIcon style={{ width: '40px', height: '40px', color: theme.palette.primary.main }} />;
        }
    };

    // Create the items array with existing items first, then add buttons
    const getAllItems = (): (MediaItem | { type: 'capture' | 'add' })[] => {
        const result: (MediaItem | { type: 'capture' | 'add' })[] = [];

        if (items.length === 0) {
            // If no items exist, show capture button first, then 2 add buttons to fill 3 visible slots
            result.push({ type: 'capture' });
            result.push({ type: 'add' });
            result.push({ type: 'add' });
        } else {
            // If items exist, show them first (leftmost position)
            result.push(...items);

            // Always add one "add" button if we haven't reached max items
            if (items.length < maxItems) {
                result.push({ type: 'add' });
            }

            // For the first 2 items, add extra "add" buttons to ensure 3 visible cards
            if (items.length < 3) {
                const extraAddButtons = 3 - items.length - 1; // -1 because we already added one above
                for (let i = 0; i < extraAddButtons; i++) {
                    result.push({ type: 'add' });
                }
            }
        } return result;
    }; const allItems = getAllItems();

    // Check if scrolling is needed and update arrow visibility
    const checkScrollPosition = () => {
        const container = scrollContainerRef.current;
        if (container) {
            const { scrollLeft, scrollWidth, clientWidth } = container;
            // More sensitive detection for mobile
            setShowLeftArrow(scrollLeft > 5);
            setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 5);
        }
    };

    React.useEffect(() => {
        // Add a small delay to ensure proper measurement
        const timer = setTimeout(() => {
            checkScrollPosition();
        }, 100);

        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', checkScrollPosition);
            const resizeObserver = new ResizeObserver(() => {
                setTimeout(checkScrollPosition, 50);
            });
            resizeObserver.observe(container);

            return () => {
                container.removeEventListener('scroll', checkScrollPosition);
                resizeObserver.disconnect();
                clearTimeout(timer);
            };
        }

        return () => clearTimeout(timer);
    }, [allItems]);

    // Auto-scroll to show the new "add" button when items are added
    React.useEffect(() => {
        const container = scrollContainerRef.current;
        if (container && items.length >= 3) {
            // When we have 3 or more items, scroll to show the add button
            // Add a delay to ensure the DOM has updated
            const timer = setTimeout(() => {
                const addButtonPosition = items.length * 108; // Each item is 100px + 8px margin
                container.scrollTo({ left: addButtonPosition - 216, behavior: 'smooth' }); // Show last 2 items + add button
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [items.length]);

    const scrollLeft = () => {
        const container = scrollContainerRef.current;
        if (container) {
            // Scroll by one item width (100px + 8px margin)
            container.scrollBy({ left: -108, behavior: 'smooth' });
        }
    };

    const scrollRight = () => {
        const container = scrollContainerRef.current;
        if (container) {
            // Scroll by one item width (100px + 8px margin)
            container.scrollBy({ left: 108, behavior: 'smooth' });
        }
    };

    const handleItemClick = (item: MediaItem | { type: 'capture' | 'add' }) => {

        // Check if it's an actual media item (has id property)
        if ('id' in item) {
            // It's a media item - show preview
            onPreview?.(item);
        } else {
            // It's a capture or add button - open gallery
            onUpload?.();
        }
    };

    const renderMediaItem = (item: MediaItem | { type: 'capture' | 'add' }, index: number) => {
        const isCapture = item.type === 'capture';
        const isAdd = item.type === 'add';
        const isMediaItem = 'id' in item;

        return (
            <Box
                key={index}
                sx={{
                    position: 'relative',
                    flexShrink: 0,
                    mr: 1,
                    width: 100, // Fixed width to prevent layout shifts
                    height: 100, // Fixed height
                }}
            >
                <IconButton
                    onClick={() => handleItemClick(item)}
                    sx={{
                        bgcolor: 'white',
                        color: theme.palette.primary.main,
                        width: '100%',
                        height: '100%',
                        borderRadius: 2,
                        border: '2px solid #e0e0e0',
                        p: 0, // Remove default padding
                        '&:hover': {
                            bgcolor: '#f9f9f9',
                            borderColor: '#d0d0d0',
                        },
                        ...(isMediaItem && item.thumbnail && mediaType !== 'audio' && {
                            backgroundImage: `url(${item.thumbnail})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            border: '2px solid #e0e0e0',
                        }),
                    }}
                >
                    {isCapture ? (
                        getMediaIcon()
                    ) : isAdd ? (
                        <AddIcon style={{ width: '40px', height: '40px', color: theme.palette.primary.main }} />
                    ) : isMediaItem && item.thumbnail && mediaType !== 'audio' ? (
                        // For uploaded media items with thumbnails (images/videos), show play button for videos
                        mediaType === 'video' ? (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    bgcolor: 'rgba(0, 0, 0, 0.6)',
                                    borderRadius: '50%',
                                    width: 40,
                                    height: 40,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <PlayArrowIcon sx={{ color: 'white', fontSize: '24px' }} />
                            </Box>
                        ) : null
                    ) : isMediaItem && mediaType === 'audio' ? (
                        // For audio items, show the microphone icon with filename
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                p: 0.5,
                                width: '100%',
                                boxSizing: 'border-box',
                            }}
                        >
                            <EarPhoneIcon style={{ width: '32px', height: '32px', color: theme.palette.primary.main }} />
                            <Typography
                                variant="caption"
                                sx={{
                                    mt: 0.5,
                                    fontSize: '8px',
                                    color: theme.palette.text.secondary,
                                    textAlign: 'center',
                                    lineHeight: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    width: '100%',
                                    maxWidth: '90px', // Explicit max width to ensure truncation
                                    px: 0.5, // Small horizontal padding to prevent edge clipping
                                }}
                            >
                                {(item.original_file_name || item.name || '').replace('_compressed', '')}
                            </Typography>
                        </Box>
                    ) : (
                        // For uploaded media items without thumbnails, show a small icon overlay
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 4,
                                right: 4,
                                bgcolor: 'rgba(255, 255, 255, 0.9)',
                                borderRadius: '50%',
                                p: 0.5,
                                fontSize: '16px',
                            }}
                        >
                            {getMediaIcon()}
                        </Box>
                    )}
                </IconButton>

                {/* Delete button for media items */}
                {isMediaItem && onRemove && (
                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemove(item.id);
                        }}
                        sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            bgcolor: 'red',
                            color: 'white',
                            width: 20,
                            height: 20,
                            fontSize: '12px',
                            '&:hover': {
                                bgcolor: 'darkred',
                            },
                            zIndex: 2,
                            minWidth: 'auto',
                            minHeight: 'auto',
                        }}
                        size="small"
                    >
                        <CloseIcon sx={{ fontSize: '12px' }} />
                    </IconButton>
                )}
            </Box>
        );
    };

    return (
        <Box
            sx={{
                p: 3,
                bgcolor: 'background.paper',
                borderRadius: 2,
                position: 'relative',
                width: '100%',
                overflow: 'hidden',
            }}
        >
            <Typography
                variant="subtitle1"
                sx={{
                    mb: 2,
                    fontFamily: "Nunito Sans Regular700",
                    fontWeight: 600,
                    color: brandMain,
                    textAlign: 'left',
                }}
            >
                {title}
            </Typography>

            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: 316, // Match scrollable container max width
                }}
            >
                {/* Left Arrow */}
                {showLeftArrow && (
                    <IconButton
                        onClick={scrollLeft}
                        sx={{
                            position: 'absolute',
                            left: { xs: -8, md: -16 },
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 2,
                            bgcolor: 'rgba(255, 255, 255, 0.95)',
                            boxShadow: 2,
                            width: { xs: 32, md: 40 },
                            height: { xs: 32, md: 40 },
                            '&:hover': {
                                bgcolor: 'white',
                                boxShadow: 3,
                            },
                        }}
                    >
                        <ChevronLeftIcon sx={{ fontSize: { xs: '18px', md: '24px' } }} />
                    </IconButton>
                )}

                {/* Scrollable Container */}
                <Box
                    ref={scrollContainerRef}
                    sx={{
                        display: 'flex',
                        overflowX: 'auto',
                        scrollbarWidth: 'none',
                        width: '100%',
                        maxWidth: 316, // Limit to 3 items: 3 * 100px + 2 * 8px margin = 316px
                        '&::-webkit-scrollbar': {
                            display: 'none',
                        },
                        pb: 1,
                        // Remove all horizontal padding to ensure perfect alignment
                    }}
                >
                    {allItems.map((item, index) => renderMediaItem(item, index))}
                </Box>

                {/* Right Arrow */}
                {showRightArrow && (
                    <IconButton
                        onClick={scrollRight}
                        sx={{
                            position: 'absolute',
                            right: { xs: -8, md: -16 }, // Moved outside container for both mobile and desktop
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 2,
                            bgcolor: 'rgba(255, 255, 255, 0.95)',
                            boxShadow: 2,
                            width: { xs: 32, md: 40 },
                            height: { xs: 32, md: 40 },
                            '&:hover': {
                                bgcolor: 'white',
                                boxShadow: 3,
                            },
                        }}
                    >
                        <ChevronRightIcon sx={{ fontSize: { xs: '18px', md: '24px' } }} />
                    </IconButton>
                )}
            </Box>

            {/* Supported formats info for image, video, and audio - shown after upload icons */}
            {mediaType === 'image' && (
                <Typography
                    variant="body2"
                    sx={{
                        mt: 2,
                        textAlign: 'left',
                        color: theme.palette.text.secondary,
                        fontFamily: theme.typography.fontFamily,
                        fontSize: '0.800rem',
                    }}
                >
                    Ondersteunde formaten: .png, .jpg, .jpeg, .heic, .heif
                </Typography>
            )}
            {mediaType === 'video' && (
                <Typography
                    variant="body2"
                    sx={{
                        mt: 2,
                        textAlign: 'left',
                        color: theme.palette.text.secondary,
                        fontFamily: theme.typography.fontFamily,
                        fontSize: '0.800rem',
                    }}
                >
                    Ondersteunde formaten: .mp4, .mov, .mkv, .hevc
                </Typography>
            )}
            {mediaType === 'audio' && (
                <Typography
                    variant="body2"
                    sx={{
                        mt: 2,
                        textAlign: 'left',
                        color: theme.palette.text.secondary,
                        fontFamily: theme.typography.fontFamily,
                        fontSize: '0.800rem',
                    }}
                >
                    Ondersteunde formaten: .mp3, .m4a, .aac, .ogg, .wav
                </Typography>
            )}
        </Box>
    );
};

export default MediaUploadSection;
